import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, LogOut } from "lucide-react";
import { ResendCountdown } from "../../components/auth/ResendCountdown";
import { OtpBoxes } from "../../components/auth/OtpBoxes";
import { toast } from "sonner";
import AuthLayout from "../../components/auth/AuthLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useAuth, homeForRole } from "../../context/AuthContext";
import { authApi, errorMessage } from "../../lib/api";
import { formatIndiaPhoneInput, isValidIndiaPhone, indiaPhoneDigits } from "../../lib/smartDefaults";

const normalize = (p) => `+91${indiaPhoneDigits(p)}`;

// Mandatory step after signup / first login: the account is unusable until a phone is OTP-verified and linked.
export default function VerifyPhone() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [state] = useState(() => {
    if (location.state?.phone) return location.state;
    try { const p = JSON.parse(sessionStorage.getItem("kt_pending_verify") || "null"); sessionStorage.removeItem("kt_pending_verify"); return p; } catch { return null; }
  });
  const [phone, setPhone] = useState(state?.phone || user?.phone || "+91 ");
  const channel = "whatsapp";
  const [req, setReq] = useState(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const autoSent = useRef(false);
  useEffect(() => { if (state?.autoSend && !autoSent.current) { autoSent.current = true; send(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!isValidIndiaPhone(phone)) return toast.error("Enter a valid 10-digit Indian mobile number (no leading 0)");
    const full = normalize(phone);
    setBusy(true);
    try {
      const r = await authApi.otpRequest({ phone: full, channel });
      setReq({ ...r, phone: full }); setOtp(""); setAttempts(0);
      toast.success(`Code sent on WhatsApp to ${full}`);
    } catch (e) { toast.error(errorMessage(e, "Could not send the code")); } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (otp.trim().length < 4) return toast.error("Enter the code we sent you");
    setBusy(true);
    try {
      const r = await authApi.linkPhone({ phone: req.phone, otp: otp.trim(), request_id: req.request_id });
      const u = r.user || r;
      setUser({ ...user, ...u, phone: u.phone || req.phone });
      toast.success("Phone verified. Welcome to KohimaTaxi!");
      navigate(homeForRole(u.role || user.role), { replace: true });
    } catch (err) {
      const n = attempts + 1; setAttempts(n);
      toast.error(n >= 5 ? "Too many wrong attempts. Request a new code." : errorMessage(err, "Invalid code"));
      if (n >= 5) { setReq(null); setOtp(""); }
    } finally { setBusy(false); }
  };

  const cancel = async () => { await logout(); navigate("/auth/login", { replace: true }); };

  const autoVerifyRef = useRef("");
  useEffect(() => {
    if (req && otp.length === 6 && !busy && autoVerifyRef.current !== otp) { autoVerifyRef.current = otp; verify(); }
  }, [otp, req, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthLayout
      title="Verify your phone"
      subtitle={`Hi ${user?.name?.split(" ")[0] || "there"} — one quick step before you can use your account.`}
      footer={<button type="button" onClick={cancel} className="inline-flex items-center gap-1.5 font-semibold text-ink-soft hover:text-ink" data-testid="verify-cancel"><LogOut className="h-3.5 w-3.5" /> Use a different account</button>}
    >
      <div className="mb-5 flex items-start gap-3 rounded-xl border border-brand-200 bg-brand-50 p-3 text-sm text-brand-700" data-testid="verify-required-note">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" /> We verify every account with a one-time code so rides, orders and payments are tied to a real phone number.
      </div>
      {!req ? (
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="vp-phone">Mobile number</Label>
            <Input id="vp-phone" type="tel" value={phone} onChange={(e) => setPhone(formatIndiaPhoneInput(e.target.value))} placeholder="+91 98765 43210" className="h-11" autoFocus data-testid="verify-phone-input" />
            <p className="text-xs text-ink-muted" data-testid="verify-channel-note">We'll send your verification code on WhatsApp.</p>
          </div>
          <Button type="submit" disabled={busy} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="verify-send-code">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send verification code</Button>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-5">
          <p className="text-sm text-ink-soft" data-testid="verify-sent-to">Code sent on WhatsApp to <span className="font-semibold text-ink">{req.phone}</span>. <button type="button" onClick={() => { setReq(null); setOtp(""); }} className="font-semibold text-brand hover:underline" data-testid="verify-change-number">Change</button></p>
          <div className="space-y-2">
            <Label htmlFor="vp-otp">6-digit code</Label>
            <OtpBoxes value={otp} onChange={(v) => setOtp(v)} autoFocus testId="verify-otp-input" />
            <p className="text-center text-xs text-ink-muted" data-testid="verify-paste-hint">Tip: you can paste the code — it verifies automatically.</p>
            {attempts > 0 && <p className="text-xs text-red-600" data-testid="verify-attempts">{attempts} wrong attempt{attempts > 1 ? "s" : ""} — {5 - attempts} left before a new code is required.</p>}
          </div>
          <Button type="submit" disabled={busy || otp.length < 4} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="verify-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Verify and continue</Button>
          <ResendCountdown onResend={send} busy={busy} nonce={req.request_id} testId="verify-resend" />
        </form>
      )}
    </AuthLayout>
  );
}
